import { useI18n } from '../../contexts/I18nContext';
import { useOverrunsReport } from '../../hooks/useTaskReports';
import type { ReportFilters } from '../../lib/taskReportsApi';
import { formatHours, ReportCard, SectionState } from './common';

// Overrun tasks (spent > estimate): per-person totals + task list, delta desc.

export function OverrunsSection({ filters }: { filters: ReportFilters }) {
  const { t } = useI18n();
  const { data, isLoading, isError } = useOverrunsReport(filters);

  return (
    <ReportCard title={t('reports.overruns.title')}>
      <SectionState isLoading={isLoading} isError={isError} />
      {data && data.tasks.length === 0 && (
        <p className="text-sm text-gray-400">{t('reports.overruns.empty')}</p>
      )}
      {data && data.tasks.length > 0 && (
        <div className="space-y-5">
          <div>
            <p className="mb-2 text-xs font-medium text-gray-500">
              {t('reports.overruns.byPerson')}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-xs">
                    <th className="px-3 py-2 text-left font-medium text-gray-500">
                      {t('reports.hours.person')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium text-gray-500">
                      {t('reports.overruns.taskCount')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium text-gray-500">
                      {t('reports.overruns.totalHours')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.byPerson.map((p) => (
                    <tr key={p.userId ?? 'unassigned'} className="border-b border-gray-100">
                      <td className="px-3 py-2 text-gray-900">
                        {p.name ?? t('reports.overruns.unassigned')}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                        {p.taskCount}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium text-red-600">
                        +{formatHours(p.totalOverrunHours)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs">
                  <th className="px-3 py-2 text-left font-medium text-gray-500">
                    {t('reports.hours.task')}
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">
                    {t('reports.hours.project')}
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">
                    {t('reports.hours.person')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500">
                    {t('reports.hours.estimated')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500">
                    {t('reports.hours.spent')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500">
                    {t('reports.hours.delta')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.tasks.map((task) => (
                  <tr key={task.id} className="border-b border-gray-100">
                    <td className="px-3 py-2 text-gray-900">
                      {task.identifier ? (
                        <span className="mr-1.5 text-xs text-gray-400">{task.identifier}</span>
                      ) : null}
                      {task.title}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{task.project?.name ?? '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{task.assignee?.name ?? '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                      {task.estimate != null ? formatHours(task.estimate) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                      {formatHours(task.spent)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium text-red-600">
                      {task.delta != null ? `+${formatHours(task.delta)}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </ReportCard>
  );
}
